import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, ArrowLeft, Sparkles, Info, Search, Share2, PackagePlus } from 'lucide-react';
import { toast } from 'sonner';
import ShareToWallModal from './ShareToWallModal';
import RailTrackActions from './RailTrackActions';
import { PreviewPlayButton, stopPreview, setPreviewQueue, clearPreviewQueue } from './PreviewPlayButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SimilarTracksModal = ({ isOpen, onClose, sourceTrack, onSearchInDiscovery, onSaveToSpotify, onShareToDiscord }) => {
  const [modes, setModes] = useState({ theory: true, artists: false, community: false });
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [sourceInfo, setSourceInfo] = useState(null);
  const [showCommunityTooltip, setShowCommunityTooltip] = useState(false);
  const [shareTrack, setShareTrack] = useState(null);
  const [digging, setDigging] = useState(false);
  const cacheRef = useRef({});

  // Current user (mirrors how the rest of the app reads auth)
  const currentUser = (() => {
    try {
      const raw = localStorage.getItem('beatseek_user');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  })();

  const activeModes = Object.entries(modes).filter(([, v]) => v).map(([k]) => k);

  // Never leave a preview playing after the modal closes.
  useEffect(() => { if (!isOpen) stopPreview(); }, [isOpen]);

  // Mini-radio: one preview rolls into the next suggestion.
  useEffect(() => {
    setPreviewQueue('ai-similar', tracks.map((t, idx) => ({
      id: `ai-${t.id || idx}`, src: t.preview_url, artist: t.artist, title: t.title,
    })));
    return () => clearPreviewQueue('ai-similar');
  }, [tracks]);

  const fetchSimilarTracks = useCallback(async () => {
    if (!sourceTrack) return;
    const cacheKey = `${sourceTrack.title}|${sourceTrack.artist}|${activeModes.sort().join(',')}`;

    if (cacheRef.current[cacheKey]) {
      setTracks(cacheRef.current[cacheKey].tracks);
      setSourceInfo(cacheRef.current[cacheKey].source_track);
      setHasSearched(true);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/discovery/similar-tracks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_track: {
            title: sourceTrack.title,
            artist: sourceTrack.artist,
            genre: sourceTrack.genre,
            bpm: sourceTrack.bpm,
            year: sourceTrack.year,
            style: sourceTrack.style,
            popularity: sourceTrack.popularity,
          },
          modes: activeModes,
        }),
      });
      if (!res.ok) throw new Error('Failed to get suggestions');
      const data = await res.json();
      setTracks(data.tracks || []);
      setSourceInfo(data.source_track);
      cacheRef.current[cacheKey] = data;
      setHasSearched(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [sourceTrack, activeModes]);

  const toggleMode = (mode) => {
    if (mode === 'community') return;
    setModes(prev => {
      const next = { ...prev, [mode]: !prev[mode] };
      if (!next.theory && !next.artists) return prev;
      return next;
    });
  };

  const digTheRun = async () => {
    if (digging || !tracks.length) return;
    const seed = sourceInfo || sourceTrack || {};
    setDigging(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/crates/dig-run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('beatseek_token') || ''}` },
        body: JSON.stringify({
          seed_title: seed.title || '',
          seed_artist: seed.artist || '',
          tracks: tracks.filter(t => t.title).map(t => ({
            track_id: `${t.artist || ''}-${t.title || ''}`.toLowerCase().replace(/\s+/g, '-'),
            source: 'beatseek_radio',
            title: t.title,
            artist: t.artist || '',
            artwork_url: t.artwork || null,
            preview_url: t.preview_url || null,
            bpm: t.bpm_key?.tempo ? Math.round(t.bpm_key.tempo) : (t.bpm || null),
          })),
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Could not save the run');
      toast.success(
        d.added ? `${d.added} track${d.added === 1 ? '' : 's'} dug into "${d.crate_name}"` : 'Already in that dig crate',
        { description: d.skipped ? `${d.skipped} already there` : 'Find it in Crate Box' }
      );
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDigging(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="similar-tracks-overlay"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        data-testid="similar-tracks-modal"
        onClick={e => e.stopPropagation()}
        className="similar-tracks-border"
        style={{
          width: '100%', maxWidth: '640px', maxHeight: '90vh',
          borderRadius: '16px', padding: '3px',
          position: 'relative',
        }}
      >
        <div style={{
          background: '#0b1120', borderRadius: '14px',
          display: 'flex', flexDirection: 'column',
          maxHeight: 'calc(90vh - 6px)', overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <button
                data-testid="similar-tracks-back"
                onClick={onClose}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: '#06b6d4', cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: 0 }}
              >
                <ArrowLeft size={14} /> Back to Results
              </button>
              <button
                onClick={onClose}
                style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', padding: '4px' }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Hero headline */}
            <div style={{ textAlign: 'center', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '2px' }}>
                <Sparkles size={18} style={{ color: '#fbbf24', filter: 'drop-shadow(0 0 6px rgba(251,191,36,0.6))' }} />
                <span style={{
                  fontSize: '22px', fontWeight: 900, letterSpacing: '1px',
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b, #fcd34d)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 10px rgba(251,191,36,0.35)) drop-shadow(0 0 20px rgba(245,158,11,0.2))',
                }}>AI's TOP 10</span>
              </div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#94a3b8', letterSpacing: '0.3px', marginTop: '3px' }}>
                Suggested Similar Tracks
              </div>
            </div>

            {/* Context line */}
            {sourceInfo && (
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', textAlign: 'center' }}>
                Similar to: <span style={{ color: '#22d3ee', fontWeight: 600 }}>"{sourceInfo.title}"</span> by <span style={{ color: '#e879f9', fontWeight: 600 }}>{sourceInfo.artist}</span>
              </div>
            )}
            {!sourceInfo && sourceTrack && (
              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', textAlign: 'center' }}>
                Similar to: <span style={{ color: '#22d3ee', fontWeight: 600 }}>"{sourceTrack.title}"</span> by <span style={{ color: '#e879f9', fontWeight: 600 }}>{sourceTrack.artist}</span>
              </div>
            )}

            {hasSearched && tracks.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: '10px' }}>
                <span style={{ fontSize: '10px', color: '#64748b' }}>Top AI Suggestions · 90%+ Match</span>
                <button
                  data-testid="similar-tracks-dig-btn"
                  onClick={digTheRun}
                  disabled={digging}
                  title="Dig the whole run into its own crate"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999,
                    background: 'rgba(247,213,92,0.08)', border: '1px solid rgba(247,213,92,0.4)',
                    color: '#f7d55c', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                    cursor: digging ? 'wait' : 'pointer',
                  }}
                >
                  <PackagePlus size={12} /> {digging ? 'Digging…' : 'Dig run'}
                </button>
              </div>
            )}

            {/* Toggle buttons */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '8px' }}>
              <button
                data-testid="mode-theory"
                onClick={() => toggleMode('theory')}
                style={{
                  padding: '5px 12px', fontSize: '10px', fontWeight: 700, borderRadius: '14px', cursor: 'pointer',
                  border: modes.theory ? '1px solid rgba(6,182,212,0.5)' : '1px solid rgba(255,255,255,0.1)',
                  background: modes.theory ? 'rgba(6,182,212,0.15)' : 'rgba(255,255,255,0.03)',
                  color: modes.theory ? '#22d3ee' : '#555', transition: 'all 0.2s',
                }}
              >Theory Match {modes.theory ? 'ON' : 'OFF'}</button>

              <button
                data-testid="mode-artists"
                onClick={() => toggleMode('artists')}
                style={{
                  padding: '5px 12px', fontSize: '10px', fontWeight: 700, borderRadius: '14px', cursor: 'pointer',
                  border: modes.artists ? '1px solid rgba(192,38,211,0.5)' : '1px solid rgba(255,255,255,0.1)',
                  background: modes.artists ? 'rgba(192,38,211,0.15)' : 'rgba(255,255,255,0.03)',
                  color: modes.artists ? '#e879f9' : '#555', transition: 'all 0.2s',
                }}
              >Similar Artists {modes.artists ? 'ON' : 'OFF'}</button>

              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button
                  data-testid="mode-community"
                  style={{
                    padding: '5px 12px', fontSize: '10px', fontWeight: 700, borderRadius: '14px',
                    border: '1px solid rgba(255,255,255,0.12)',
                    background: 'rgba(255,255,255,0.03)',
                    color: '#556', cursor: 'default', opacity: 0.65,
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}
                >
                  BeatSeekers Also Like
                  <span
                    onMouseEnter={() => setShowCommunityTooltip(true)}
                    onMouseLeave={() => setShowCommunityTooltip(false)}
                    style={{ cursor: 'help' }}
                  >
                    <Info size={10} style={{ color: '#667' }} />
                  </span>
                </button>
                {showCommunityTooltip && (
                  <div style={{
                    position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)',
                    background: 'rgba(15,15,25,0.98)', border: '1px solid rgba(6,182,212,0.2)',
                    borderRadius: '8px', padding: '8px 12px', fontSize: '10px', color: '#94a3b8',
                    whiteSpace: 'nowrap', zIndex: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                  }}>
                    Coming soon — as our community grows, we'll recommend what other BeatSeekers are loving
                  </div>
                )}
              </div>
            </div>

            {/* Find button — only shows before first search */}
            {!hasSearched && (
              <button
                data-testid="similar-tracks-search"
                onClick={fetchSimilarTracks}
                disabled={loading || activeModes.length === 0}
                style={{
                  width: '100%', padding: '8px', fontSize: '11px', fontWeight: 700, borderRadius: '8px',
                  border: 'none', cursor: loading ? 'wait' : 'pointer',
                  background: 'linear-gradient(135deg, rgba(6,182,212,0.2), rgba(192,38,211,0.2))',
                  color: '#fff', transition: 'all 0.2s',
                  opacity: loading || activeModes.length === 0 ? 0.5 : 1,
                }}
              >
                {loading ? 'Finding matches...' : 'Find Similar Tracks'}
              </button>
            )}

            {hasSearched && (
              <div style={{ fontSize: '10px', color: '#7a8ba8', marginTop: '4px', textAlign: 'center', fontStyle: 'italic' }}>
                For updated results, change option selections or make a new search
              </div>
            )}
          </div>

          {/* Content area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
            {loading && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', gap: '16px' }}>
                {/* Spinning vinyl */}
                <div className="similar-tracks-vinyl-spin" style={{
                  width: '60px', height: '60px', borderRadius: '50%', position: 'relative',
                  background: 'radial-gradient(ellipse at 30% 30%, rgba(255,255,255,0.15) 0%, transparent 50%), linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 25%, #1a1a1a 50%, #0a0a0a 75%, #1a1a1a 100%)',
                  boxShadow: 'inset 0 0 10px rgba(0,0,0,0.9), 0 0 16px rgba(0,255,255,0.3), 0 0 32px rgba(255,0,255,0.15)',
                }}>
                  <div style={{ position: 'absolute', inset: '6px', borderRadius: '50%', background: 'repeating-radial-gradient(circle at center, transparent 0px, transparent 2px, rgba(255,255,255,0.08) 2px, rgba(255,255,255,0.08) 3px)' }}></div>
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '18px', height: '18px', borderRadius: '50%', background: 'linear-gradient(135deg, #00ffff, #ff00ff)', boxShadow: '0 0 10px rgba(0,255,255,0.5)' }}></div>
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '6px', height: '6px', borderRadius: '50%', background: 'radial-gradient(circle at 30% 30%, #333, #000)', border: '1px solid rgba(255,255,255,0.4)' }}></div>
                </div>
                <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>Finding your perfect matches...</div>
                <div style={{ fontSize: '10px', color: '#475569' }}>Analyzing {activeModes.includes('theory') ? 'music theory' : ''}{activeModes.includes('theory') && activeModes.includes('artists') ? ' & ' : ''}{activeModes.includes('artists') ? 'artist similarities' : ''}</div>
              </div>
            )}

            {error && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#ef4444', fontSize: '12px' }}>
                {error}
              </div>
            )}

            {!loading && !error && hasSearched && tracks.length === 0 && (
              <div style={{ textAlign: 'center', padding: '30px', color: '#555', fontSize: '12px' }}>
                No tracks found with 90%+ match. Try different mode combinations.
              </div>
            )}

            {!loading && tracks.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {tracks.map((track, idx) => (
                  <div
                    key={track.id || idx}
                    data-testid={`similar-track-${idx}`}
                    style={{
                      padding: '12px', borderRadius: '10px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {/* Track info + match badge */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                      <div style={{
                        position: 'relative', flex: '0 0 auto', width: 48, height: 48, borderRadius: 8, overflow: 'hidden',
                        background: 'linear-gradient(135deg, #10243a, #1b0f2e)', border: '1px solid rgba(255,255,255,0.08)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {track.artwork ? (
                          <img src={track.artwork} alt="" loading="lazy" data-testid={`similar-track-art-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <span style={{ fontSize: 16, fontWeight: 800, color: 'rgba(255,255,255,0.6)' }}>{(track.artist || '?').charAt(0).toUpperCase()}</span>
                        )}
                        {(track.preview_url || (track.artist && track.title)) && (
                          <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PreviewPlayButton src={track.preview_url} id={`ai-${track.id || idx}`} queueId="ai-similar" artist={track.artist} title={track.title} size={12} testId={`similar-preview-${idx}`} />
                          </span>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: '2px' }}>{track.title}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{track.artist}</div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                          {track.genre && <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(6,182,212,0.08)', color: '#22d3ee', fontWeight: 600 }}>{track.genre}</span>}
                          {(track.bpm_key?.tempo || track.bpm) && (
                            <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(255,170,0,0.08)', color: '#fbbf24', fontWeight: 600 }} data-testid="similar-track-bpm">
                              🥁 {Math.round(track.bpm_key?.tempo || track.bpm)} BPM
                            </span>
                          )}
                          {track.bpm_key?.key_name && (
                            <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(192,38,211,0.10)', color: '#e879f9', fontWeight: 600 }} data-testid="similar-track-key">
                              🎹 {track.bpm_key.key_name}{track.bpm_key.scale === 'minor' ? 'm' : ''}
                            </span>
                          )}
                          {track.year && <span style={{ fontSize: '9px', color: '#555' }}>{track.year}</span>}
                        </div>
                        {track.match_reasons && (
                          <div style={{ fontSize: '9px', color: '#64748b', marginTop: '5px', fontStyle: 'italic', lineHeight: 1.4 }}>{track.match_reasons}</div>
                        )}
                      </div>
                      {track.match_score && (
                        <span style={{
                          padding: '2px 8px', borderRadius: '10px', fontSize: '9px', fontWeight: 700, flexShrink: 0,
                          background: track.match_score >= 95 ? 'rgba(0,255,136,0.15)' : 'rgba(6,182,212,0.12)',
                          color: track.match_score >= 95 ? '#00ff88' : '#22d3ee',
                          border: `1px solid ${track.match_score >= 95 ? 'rgba(0,255,136,0.3)' : 'rgba(6,182,212,0.25)'}`,
                        }}>
                          {track.match_score}%
                        </span>
                      )}
                    </div>

                    {/* Action buttons row */}
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <RailTrackActions
                        track={track}
                        idx={idx}
                        zIndex={100010}
                        onSaveToSpotify={onSaveToSpotify}
                        onShareToDiscord={onShareToDiscord}
                      />
                      <button
                        data-testid={`similar-share-wall-${idx}`}
                        onClick={() => setShareTrack(track)}
                        title="Share to The Wall"
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                          padding: '6px 10px', fontSize: '10px', fontWeight: 700,
                          borderRadius: '7px', cursor: 'pointer',
                          border: '1px solid rgba(34,211,238,0.4)',
                          background: 'rgba(34,211,238,0.1)',
                          color: '#67e8f9', transition: 'all 0.2s',
                          flexShrink: 0,
                        }}
                      >
                        <Share2 size={10} /> Share
                      </button>
                      <button
                        data-testid={`search-discovery-${idx}`}
                        onClick={() => {
                          if (onSearchInDiscovery) {
                            onSearchInDiscovery(`${track.artist} ${track.title}`);
                            onClose();
                          }
                        }}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                          padding: '6px 12px', fontSize: '10px', fontWeight: 700,
                          borderRadius: '7px', cursor: 'pointer',
                          border: '1px solid rgba(192,38,211,0.35)',
                          background: 'rgba(192,38,211,0.1)',
                          color: '#e879f9', transition: 'all 0.2s',
                        }}
                      >
                        <Search size={10} /> Search in Discovery
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && !hasSearched && !error && (
              <div style={{ textAlign: 'center', padding: '30px', color: '#555', fontSize: '12px' }}>
                <Sparkles size={20} style={{ color: '#c026d3', marginBottom: '8px', opacity: 0.5 }} />
                <p style={{ marginBottom: '4px' }}>Select your preferred modes and hit Find Similar Tracks</p>
                <p style={{ fontSize: '10px', color: '#444' }}>AI will analyze and find the best 90%+ matches</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share to The Wall (Phase 2) */}
      <ShareToWallModal
        isOpen={!!shareTrack}
        onClose={() => setShareTrack(null)}
        source="similar"
        track={shareTrack}
        currentUser={currentUser}
      />
    </div>
  );
};

export default SimilarTracksModal;
